import React, { useMemo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { ACCESS_POLICY, EXTERNAL_PRODUCTS, KYC } from 'constants/constants';
import { getClientId } from 'utils';
import { useGetContactGroups } from 'hooks/rest/useGetContactGroups';
import { ContactGroupEnum } from 'components/Connections/types';
import { useClientProfile } from 'prodivers/clientProfile';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import SinglePortfolioContext from './context';
import { navigationPages } from '../components/SinglePortfolioLayout/constants';
import {
    portfolioSelector,
    usePortfolioSelector,
} from '../../../../../../../domain/Portfolio';
import { useProductsList } from '../../../../../../../hooks/useProducts';
import Preloader from '../../../../../../../components/Preloader';
import SinglePortfolioLayout from '../components/SinglePortfolioLayout/SinglePortfolioLayout';
import { useStrategy } from '../pages/Strategy/hooks/useStrategy';
import { NEW_3B_RETURN_DATE, NEW_RETURN_DATE } from '../constants';
import { dateFormatZurich } from '../../../../../../../utils/datetime';
import {
    isAnyOfThreeBProductTypes,
    useThreeBProductType,
} from '../../../../../../../hooks/isThreeBProduct';
import { useManagerProfile } from '../../../../../../../prodivers/managerProfile';
import arrayContains from '../../../../../../../utils/arrayUtils';

// 1. Define robust interfaces for your state
interface ClientData {
    isLockCodeChecked: boolean;
    clientDisplayName: string;
    blockingCode: string | number;
    grund: string;
    refNumber: string | number;
    shippingMethod: string;
}

interface SinglePortfolioState {
    portfolioDetail: any; // Ideally replace 'any' with your Portfolio type
    portfolioDetailRaw: any;
    navigation: ReturnType<typeof navigationPages>;
    portfolioName: string;
    productName: string;
    productNameRisk: string;
    selectedProductSettings: any;
    isMigratedPortfolio: boolean;
    hasAccessToChangeStrategy: boolean;
    migrationDate: string | null;
    isPortfolioChangeable: boolean;
    is3B: boolean;
    is3ap: boolean;
    isVBOrVBP: boolean;
    clientProfile: any;
    mainClientData: ClientData;
    jointClientData?: ClientData;
    riskCategories: {text: string}[];
    // ... include spread values from productType and strategy hooks
    [key: string]: any;
}

interface TSinglePortfolioProviderProps {
    dfsClientId: string;
    portfolioId: number;
    children: ReactNode;
}

const SinglePortfolioProvider = ({
    dfsClientId,
    portfolioId,
    children,
}: TSinglePortfolioProviderProps) => {
    const { t } = useTranslation();
    const { hasAccessPolicy } = useManagerProfile();
    const contactId = getClientId(dfsClientId);

    const strategy = useStrategy({ portfolioId, dfsClientId });
    const clientProfile = useClientProfile();

    const portfolioDetail = usePortfolioSelector(portfolioSelector);
    const { isLoadingProducts, products } = useProductsList() as any;
    const {
        data, dataRaw, isLoading, error,
    } = portfolioDetail || {};

    const { data: clientDetails } = useGetCustomService({
        service: 'clientService',
        api: 'getContact',
        payload: contactId,
        enabled: !!contactId,
    });

    const { data: contactGroups } = useGetContactGroups({
        contactId,
        isEnabled: true,
    });

    const availableJointAccount = useMemo(
        () => contactGroups?.find(
            (group) => group?.groupName === ContactGroupEnum.ja,
        ),
        [contactGroups],
    );

    const { data: jointAccountContactGroupDetails } = useGetCustomService({
        service: 'contactManagement',
        api: 'getContactsGroupDetails',
        payload: { contactGroupId: availableJointAccount?.groupId },
        enabled: !!availableJointAccount?.groupId,
    });

    // Parsed logic for JA custom attributes
    const jaLockCodeData = useMemo(() => {
        const attributes = jointAccountContactGroupDetails?.data?.customAttributes;

        if (!attributes) return {};
        try {
            return JSON.parse(attributes)?.LockCode || {};
        } catch (e) {
            return {};
        }
    }, [jointAccountContactGroupDetails?.data?.customAttributes]);

    const jaClientDisplayNames = useMemo(
        () => {
            const owners = Object.values(availableJointAccount?.owners || {});
            const names = owners.join(' & ');

            return names ? `${names} (${availableJointAccount?.externalId || ''})` : '';
        },
        [availableJointAccount],
    );

    const selectedProduct = useMemo(
        () => products?.find((f) => f.id === data?.productId),
        [products, data?.productId],
    );

    const selectedProductSettings = selectedProduct?.settings;

    const productType = useThreeBProductType({
        product: { externalId: selectedProduct?.externalId },
        isJointAccount: dataRaw?.IsJointAccount,
    });

    const is3B = useMemo(
        () => isAnyOfThreeBProductTypes(selectedProduct?.externalId),
        [selectedProduct?.externalId],
    );

    const is3ap = useMemo(
        () => arrayContains([EXTERNAL_PRODUCTS.aaap], selectedProduct?.externalId),
        [selectedProduct?.externalId],
    );

    const isVBOrVBP = useMemo(
        () => arrayContains([EXTERNAL_PRODUCTS.fz, EXTERNAL_PRODUCTS.fzp], selectedProduct?.externalId),
        [selectedProduct?.externalId],
    );

    const returnDate = is3B ? NEW_3B_RETURN_DATE : NEW_RETURN_DATE;

    const isMigratedPortfolio = useMemo(
        () => moment(returnDate, dateFormatZurich).isAfter(
            moment(data?.customAttributes?.CreateDate, dateFormatZurich),
        ),
        [data?.customAttributes?.CreateDate, returnDate],
    );

    const migrationDate = isMigratedPortfolio ? returnDate : null;

    const isPortfolioChangeable = useMemo(
        () => strategy.isPortfolioChangeable
            && hasAccessPolicy(selectedProductSettings?.AccessPolicy),
        [strategy.isPortfolioChangeable, selectedProductSettings?.AccessPolicy, hasAccessPolicy],
    );

    const hasAccessToChangeStrategy = useMemo(() => {
        const isCHResident = clientDetails?.data?.details?.personalInformation?.primaryAddressCountry === KYC.swissNationality;
        const hasProperAccessPolicy = hasAccessPolicy(ACCESS_POLICY.AdaptPortfolioForNonResident);

        return isCHResident || hasProperAccessPolicy;
    }, [clientDetails?.data?.details?.personalInformation?.primaryAddressCountry, hasAccessPolicy]);

    const navigation = navigationPages({
        portfolioId,
        dfsClientId,
        t,
        showDeposits: !!(selectedProductSettings?.showDeposits && !is3B && !isVBOrVBP),
        showDetails: true,
        showStrategy: !!selectedProductSettings?.showStrategy,
        showCases: is3B,
    });

    const state: SinglePortfolioState = useMemo(
        () => ({
            portfolioDetail: data,
            portfolioDetailRaw: dataRaw,
            navigation,
            portfolioName: data?.title ?? '',
            productName: data?.overview?.product,
            productNameRisk: data?.overview
                ? `${data.overview.product}- ${data.overview.strategy}`
                : '',
            selectedProductSettings: selectedProductSettings ?? {},
            isMigratedPortfolio,
            hasAccessToChangeStrategy,
            migrationDate,
            ...productType,
            ...strategy,
            isPortfolioChangeable,
            is3B,
            is3ap,
            isVBOrVBP,
            clientProfile,
            mainClientData: {
                isLockCodeChecked: (clientDetails?.data?.details?.personalInformation?.extBlockingCode ?? 0) >= 7,
                clientDisplayName: clientDetails?.data?.details?.personalInformation?.displayName || '-',
                blockingCode: clientDetails?.data?.details?.personalInformation?.extBlockingCode ?? '-',
                grund: clientDetails?.data?.details?.personalInformation?.extBlockingReason || '-',
                refNumber: clientDetails?.data?.details?.personalInformation?.extBlockingCustomerId || '-',
                shippingMethod: clientDetails?.data?.details?.personalInformation?.extBlockingMailingType || '-',
            },
            ...(jointAccountContactGroupDetails && {
                jointClientData: {
                    isLockCodeChecked: (jaLockCodeData?.extBlockingCode ?? 0) > 0,
                    clientDisplayName: jaClientDisplayNames || '--',
                    blockingCode: jaLockCodeData?.extBlockingCode ?? '-',
                    grund: jaLockCodeData?.extBlockingReason || '-',
                    refNumber: jaLockCodeData?.extBlockingCustomerId || '-',
                    shippingMethod: jaLockCodeData?.extBlockingMailingType || '-',
                },
            }),
        }),
        [
            data, dataRaw, navigation, selectedProductSettings,
            isMigratedPortfolio, hasAccessToChangeStrategy, migrationDate,
            productType, strategy, isPortfolioChangeable, is3B, is3ap,
            isVBOrVBP, clientProfile, clientDetails?.data?.details?.personalInformation, jointAccountContactGroupDetails,
            jaLockCodeData, jaClientDisplayNames,
        ],
    );

    return (
        <SinglePortfolioContext.Provider value={state}>
            <Preloader isLoading={isLoading || isLoadingProducts} error={error}>
                <SinglePortfolioLayout portfolioId={portfolioId} navigation={navigation}>
                    {children}
                </SinglePortfolioLayout>
            </Preloader>
        </SinglePortfolioContext.Provider>
    );
};

export default SinglePortfolioProvider;
